import classNames from 'classnames';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import * as events from '../../lib/analytics/events';
import { centsToDollarsNumber } from '../../lib/util';
import styles from '../../styles/lineItem.module.scss';
import ErrorMessage from '../ErrorMessage';
import RemoveLineItemButton from '../RemoveLineItemButton';
import { useDispatch } from 'react-redux';
import CheckoutContext from '../../lib/CheckoutContext';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check.svg';
import { skusForCartItem } from '../../lib/cart';
import { getProductsBySku } from '../../lib/product';
import * as types from '../../types';
import store from '../../lib/reduxStore';
import { useCartPricing } from '../../contexts/CartPricingContext';
import { SERIES_3_UPGRADE_V2_COUPON_CODE } from '../../views/Series3UpgradeV2/Series3UpgradePDPV2';

interface CouponLineItemProps {
  allowRemoveCoupon?: boolean;
  appliedDiscountInCents?: number;
  couponCode: string;
  validationError?: string | null;
  appliedDiscountInMonths?: number;
}

export function inCartCollarCount(
  cartItems: types.StoreCartItem[],
  filters?: {
    includeSkus?: string[] | undefined;
    excludeSkus?: string[] | undefined;
  },
  ids: string[] = [types.series3CollarId, types.series3PlusCollarId],
): number {
  let totalQty = 0;
  const products = store.getState().config.products;
  const productsBySku = getProductsBySku(products);
  for (const cartItem of cartItems) {
    const skus = skusForCartItem(cartItem);
    const qty = cartItem.quantity;
    for (const sku of skus) {
      const product = productsBySku.get(sku);
      if (!product) {
        continue;
      }
      if (types.isModuleSubscriptionProduct(product)) {
        if (filters?.includeSkus && !filters.includeSkus.includes(product.sku)) {
          continue;
        }
        if (filters?.excludeSkus && filters.excludeSkus.includes(product.sku)) {
          continue;
        }
        if (ids.some((id) => product.canBePurchasedForProductIds.includes(id))) {
          totalQty += qty;
        }
      }
    }
  }
  return totalQty;
}

export function inCartS3CollarCount(
  cartItems: types.StoreCartItem[],
  filters?: {
    includeSkus?: string[] | undefined;
    excludeSkus?: string[] | undefined;
  },
): number {
  return inCartCollarCount(cartItems, filters, [types.series3CollarId, types.series3PlusCollarId]);
}

export function inCartMiniCollarCount(
  cartItems: types.StoreCartItem[],
  filters?: {
    includeSkus?: string[] | undefined;
    excludeSkus?: string[] | undefined;
  },
): number {
  return inCartCollarCount(cartItems, filters, [types.miniCollarId]);
}

export function inCartNonPrepaidMembershipCount(cartItems: types.StoreCartItem[]): number {
  return inCartS3CollarCount(cartItems, {
    includeSkus: ['sub-monthly-1m-001', 'sub-monthly-3m-001', 'sub-f3b-1m-001', 'sub-f3b-3m-001'],
  });
}

export function inCartPrepaidMembershipCount(cartItems: types.StoreCartItem[]): number {
  return inCartS3CollarCount(cartItems, {
    excludeSkus: ['sub-monthly-1m-001', 'sub-monthly-3m-001', 'sub-f3b-1m-001', 'sub-f3b-3m-001'],
  });
}

export function couponWaivesActivationFee(cartPricing: types.CartPricing, cart: types.Cart): boolean {
  return inCartS3CollarCount(Object.values(cart.cartItems)) > 0 && !!cartPricing.waivedActivationFeesInCents;
}

export default function CouponLineItemV2({
  allowRemoveCoupon,
  couponCode,
  validationError,
  appliedDiscountInMonths,
  appliedDiscountInCents,
}: CouponLineItemProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { checkoutActions, cart } = useContext(CheckoutContext);
  const contextCartPricing = useCartPricing();

  if (validationError) {
    return null;
  }

  if (couponCode === SERIES_3_UPGRADE_V2_COUPON_CODE) {
    return null;
  }

  const s3CollarCount = inCartS3CollarCount(Object.values(cart.cartItems));
  const shouldWaiveActivationFee = couponWaivesActivationFee(contextCartPricing, cart);

  const isDiscountAmountSetupFee =
    Math.abs(contextCartPricing.totalInCents - contextCartPricing.subtotalInCents) === 2000;
  const isCouponSetupFeeOnly =
    contextCartPricing.couponDiscount?.type === 'dollars' &&
    contextCartPricing.couponDiscount.skus?.includes('setup-fee');

  // Hack - Coupons that only waive activation fee are marked as "setup-fee" in the backend
  const isOnlyActivationFeeWaived =
    s3CollarCount === 1 ? isDiscountAmountSetupFee && isCouponSetupFeeOnly : isCouponSetupFeeOnly;

  const showLineItemPrice = !!appliedDiscountInCents && !appliedDiscountInMonths && !isOnlyActivationFeeWaived;
  let lineItemPriceText = '';
  if (showLineItemPrice) {
    const discountAmount = centsToDollarsNumber(appliedDiscountInCents);
    lineItemPriceText =
      s3CollarCount > 0
        ? t('off_prepaid_membership_one_time', { amount: discountAmount })
        : t('off_one_time', { amount: discountAmount });
  }

  return (
    <div className={classNames('coupon-line-item-v2', styles.lineItem, { [styles.noRemove]: !allowRemoveCoupon })}>
      {allowRemoveCoupon && (
        <div className={styles.removeButtonRow}>
          <RemoveLineItemButton
            onClick={() => {
              dispatch(checkoutActions.removeCoupon({}));
              events.cartPage.couponRemoved(couponCode);
            }}
          />
        </div>
      )}
      <div className={styles.itemRow}>
        <div className={classNames(styles.lineItemImage, styles.empty)}></div>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>
            <div className={styles.lineItemPriceRow}>
              <div className={styles.lineItemPromoContainer}>
                <div className={styles.lineItemPromoApplied}>
                  {t('promo_applied', { couponCode: couponCode.toUpperCase() })}
                </div>
                {!!appliedDiscountInMonths && (
                  <div className={styles.lineItemPrice}>
                    <CheckIcon />{' '}
                    {appliedDiscountInMonths > 1
                      ? t('extra_months_free_value', {
                          count: appliedDiscountInMonths,
                          value: appliedDiscountInMonths * 19,
                        })
                      : t('extra_month_free_value', {
                          count: appliedDiscountInMonths,
                          value: appliedDiscountInMonths * 19,
                        })}
                  </div>
                )}
                {showLineItemPrice && (
                  <div className={styles.lineItemPrice}>
                    <CheckIcon /> {lineItemPriceText}
                  </div>
                )}
                {shouldWaiveActivationFee && s3CollarCount && (
                  <div className={styles.lineItemPrice}>
                    <CheckIcon /> {t('no_activation_fee_off', { amount: s3CollarCount * 20 })}
                  </div>
                )}
              </div>
            </div>
            {!!validationError && (
              <div className={styles.lineItemPriceRow}>
                <ErrorMessage errors={[validationError]} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
